import React, {useRef, useState} from 'react';
import * as XLSX from 'xlsx';
import {useMutation, useQuery} from '@tanstack/react-query';
import {SVG} from '../../../../../constants/assetLists';
import {useSnackbar} from '../../../../../context/SnackbarContextProvider';
import {handleBulkImportChecklistService} from '../../../../../services/operations/checklist';
import {handleGetAllDepartmentsService} from '../../../../../services/operations/departments';
import AppButton from '../../../../../components/common/AppButton';
import AppMenu from '../../../../../components/common/AppMenu';
import ImportExcelOptionsDialog from '../../../../users/components/UserScreens/components/ImportExcelOptionsDialog';
import {useChecklistContext} from '../../../../../context/checklist/ChecklistContextProvider';
import {useClientsContext, BranchData} from '../../../../../context/clients/ClientsContextProvider';
import {useOperationsContext} from '../../../../../context/OperationsContextProvider';
import QUERY_KEYS from '../../../../../constants/queryKeys';

const IMPORT_MENUITEMS = [
  {
    title: 'יבוא צ’קליסט מקובץ אקסל',
    value: 'import',
    icon: SVG.excelGreenIcon,
  },
];

function parseSheet(ws: XLSX.WorkSheet) {
  // 1) Rows as arrays
  const rows = XLSX.utils.sheet_to_json(ws, {header: 1, defval: ''}) as any[][];

  // 2) Remove leading/trailing empty rows
  const nonEmptyRows = rows.filter(r => r.some(c => String(c).trim() !== ''));
  if (!nonEmptyRows.length) return [];

  // 3) If first row is that "Table 1" junk, drop it
  const junkFirstRow = nonEmptyRows[0].every(
    (c, i) =>
      (i === 0 && String(c).trim().toLowerCase().includes('table')) ||
      String(c).trim() === '',
  );
  const headerRow = junkFirstRow ? (nonEmptyRows[1] ?? []) : nonEmptyRows[0];
  const dataRows = junkFirstRow ? nonEmptyRows.slice(2) : nonEmptyRows.slice(1);

  // 4) Normalize headers and keep only non-empty columns
  const rawHeaders = headerRow.map(h => String(h).trim());
  const keepIdx: number[] = [];
  const headers = rawHeaders
    .map((h, i) => {
      if (h) keepIdx.push(i);
      return h;
    })
    .filter(Boolean);

  // 5) Map data rows to objects using only kept columns
  const json = dataRows.map(r => {
    const obj: Record<string, any> = {};
    keepIdx.forEach((colIdx, j) => {
      obj[headers[j]] = r[colIdx] ?? '';
    });
    return obj;
  });

  return json;
}

function AddChecklists() {
  const {showSnackbar} = useSnackbar();

  const {setShowAddEditDialog, refetchGroupedChecklists} = useChecklistContext();
  const {selectedLocation} = useClientsContext();
  const {shiftTimeList} = useOperationsContext();

  // Fetch departments for name-to-ID matching
  const {data: allDepartments} = useQuery({
    queryKey: [QUERY_KEYS.GET_ALL_DEPARTMENTS, selectedLocation?.id],
    queryFn: () =>
      handleGetAllDepartmentsService({
        clientId: selectedLocation?.isClient ? selectedLocation?.id : selectedLocation?.clientId,
        branchId: !selectedLocation?.isClient
          ? selectedLocation?.id
          : undefined,
      }),
    enabled: !!selectedLocation,
  });

  // Helper to match department names to IDs
  // If "כל המחלקות" is provided, returns 'all' string
  const matchDepartmentNames = (names: string[]): string[] | 'all' => {
    // Check for "all departments" in Hebrew
    if (names.some(n => n.trim() === 'כל המחלקות')) {
      return 'all';
    }

    // Match each name to its ID
    return names
      .map(name => {
        const dept = allDepartments?.departments?.find(
          (d: any) => d.name.toLowerCase().trim() === name.toLowerCase().trim(),
        );
        return dept?._id;
      })
      .filter(Boolean) as string[];
  };

  // Helper to match shift time names to IDs
  // If "כל המשמרות" is provided, returns all shift IDs
  const matchShiftNames = (names: string[]): string[] => {
    // Check for "all shifts" in Hebrew - return all shift IDs
    if (names.some(n => n.trim() === 'כל המשמרות')) {
      return shiftTimeList?.shifts?.map((s: any) => s._id) || [];
    }

    // Match each name to its ID
    return names
      .map(name => {
        const shift = shiftTimeList?.shifts?.find(
          (s: any) => s.name.toLowerCase().trim() === name.toLowerCase().trim(),
        );
        return shift?._id;
      })
      .filter(Boolean) as string[];
  };

  const [importAnchorTaskEl, setImportAnchorTaskEl] =
    useState<HTMLElement | null>(null);
  const [importedChecklists, setImportedChecklists] = useState<any>(null);
  const [showExcelOptionsDialog, setShowExcelOptionsDialog] = useState(false);
  const [isDuplicateErr, setIsDuplicateErr] = useState(false);

  const excelInputRef = useRef<HTMLInputElement>(null);

  const handleImportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setImportAnchorTaskEl(event.currentTarget as HTMLElement);
  };

  const handleModalOpen = (e: React.MouseEvent) => {
    e.stopPropagation();

    setShowAddEditDialog(true);
  };

  const handleImportMenuClose = () => {
    setImportAnchorTaskEl(null);
  };

  const handleImportMenuItemClick = (val: string) => {
    if (val === 'import') {
      excelInputRef.current?.click();
    }
  };

  const isValidData = (jsonData: any) => {
    if (!jsonData || jsonData.length === 0) return false;
    const keys = Object.keys(jsonData[0]);
    // Required fields: title, departments
    if (keys.length > 0 && keys.includes('title') && keys.includes('departments')) {
      return true;
    }
    return false;
  };

  const handleExcelPicked: React.ChangeEventHandler<
    HTMLInputElement
  > = async e => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check if selectedLocation is available
    if (!selectedLocation) {
      showSnackbar({
        type: 'error',
        message: 'יש לבחור לקוח או סניף מהתפריט הצדדי לפני היבוא.',
      });
      return;
    }

    // Check if departments and shifts data is loaded
    if (!allDepartments?.departments || !shiftTimeList?.shifts) {
      showSnackbar({
        type: 'error',
        message: 'טוען נתונים, אנא נסה שוב בעוד רגע.',
      });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async e => {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, {type: 'array'});
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = parseSheet(worksheet);

        if (isValidData(jsonData)) {
          // Validate each checklist - must have title and departments
          const validChecklists = jsonData.filter((item: any) => {
            return item?.title?.trim() && item?.departments?.trim();
          });

          if (validChecklists.length === 0) {
            showSnackbar({
              type: 'error',
              message: 'לא נמצאו צ׳קליסטים תקינים בקובץ.',
            });
            return;
          }

          // Helper to parse boolean values from Excel
          const parseBoolean = (val: any): boolean => {
            if (typeof val === 'boolean') return val;
            if (typeof val === 'string') {
              const lower = val.toLowerCase().trim();
              return lower === 'true' || lower === 'כן' || lower === '1';
            }
            return false;
          };

          // Helper to parse comma-separated values
          const parseCommaSeparated = (val: any): string[] => {
            if (!val) return [];
            return String(val)
              .split(',')
              .map(s => s.trim())
              .filter(Boolean);
          };

          // Derive clientId and branchIds from selectedLocation
          const clientId = selectedLocation.isClient
            ? selectedLocation.id
            : (selectedLocation as BranchData).clientId;

          const branchIds = selectedLocation.isClient
            ? []
            : [selectedLocation.id];

          setImportedChecklists(() =>
            validChecklists.map((item: any) => {
              const statusValue =
                item.status?.toLowerCase() === 'inactive' ? 'inactive' : 'active';

              // Match department names to IDs
              const departmentNames = parseCommaSeparated(item.departments);
              const departments = matchDepartmentNames(departmentNames);

              // Match shift time names to IDs
              const shiftNames = parseCommaSeparated(item.shiftTime);
              const shiftTime = matchShiftNames(shiftNames);

              return {
                title: item.title?.trim(),
                description: item.description?.trim() || '',
                // clientIds from selectedLocation with status
                clientIds: [{_id: clientId, status: statusValue}],
                // branchIds from selectedLocation with status
                branchIds: branchIds.map(branchId => ({
                  _id: branchId,
                  status: statusValue,
                })),
                departments,
                shiftTime,
                showInBrief: parseBoolean(item.showInBrief),
                approvalRequired: parseBoolean(item.approvalRequired),
                commentsEnabled: parseBoolean(item.commentsEnabled),
                list: parseCommaSeparated(item.list).map(taskDetail => ({
                  details: taskDetail,
                  activeStatus: 'active',
                })),
              };
            }),
          );
          setShowExcelOptionsDialog(true);
        } else {
          showSnackbar({
            type: 'error',
            message: 'פורמט קובץ לא תקין. נדרשות עמודות: title, departments',
          });
          return;
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (err: any) {
      console.error(err);
    } finally {
      e.target.value = '';
    }
  };

  const handleOptionClick = (opt: string) => {
    const payload = {
      checklists: importedChecklists,
      duplicateAction: opt, // 'replace', 'skip', or 'hold'
    };
    handleBulkImportChecklist(payload);
  };

  const handleExcelDialogClose = () => {
    setImportedChecklists(null);
    setIsDuplicateErr(false);
    setShowExcelOptionsDialog(false);
  };

  const {mutate: handleBulkImportChecklist, isPending: isAdding} = useMutation({
    mutationFn: handleBulkImportChecklistService,
    onSuccess: data => {
      showSnackbar({
        message: data?.message ?? 'הצ׳קליסטים יובאו בהצלחה',
        type: 'success',
      });
      refetchGroupedChecklists();
      handleExcelDialogClose();
    },
    onError: (error: any) => {
      // Check if this is a duplicate error (409 Conflict)
      if (
        error?.response?.status === 409 &&
        error?.response?.data?.isDuplicateError
      ) {
        // Show dialog with only replace/skip options
        setIsDuplicateErr(true);
        // Keep dialog open, don't close
      } else {
        // Other errors - show snackbar and close
        showSnackbar({
          message: error?.response?.data?.message ?? 'שגיאה ביבוא הצ׳קליסטים',
          type: 'error',
        });
        handleExcelDialogClose();
      }
    },
  });

  return (
    <>
      <div>
        <AppButton
          variant="primary"
          py="12px"
          iconStart={
            <div className="w-[12px] h-[12px]">
              <img
                src={SVG.addWhiteIcon}
                alt="icon"
                className="w-full h-full object-contain"
              />
            </div>
          }
          iconEnd={
            <AppMenu
              anchorEl={importAnchorTaskEl}
              showDivider
              dividerIndex={1}
              itemIconWidth={24}
              itemIconHeight={24}
              open={Boolean(importAnchorTaskEl)}
              onOpen={handleImportMenuOpen}
              onClose={handleImportMenuClose}
              items={IMPORT_MENUITEMS}
              onItemClick={handleImportMenuItemClick}
              menuIcon={
                <div className="w-[12px] h-[6px]">
                  <img
                    src={SVG.dropdownWhiteIcon}
                    alt="icon"
                    className="w-full h-full object-contain"
                  />
                </div>
              }
            />
          }
          onClick={handleModalOpen}>
          הוספה
        </AppButton>
        {/* Hidden file input for import excel */}
        <input
          ref={excelInputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleExcelPicked}
        />
      </div>

      <ImportExcelOptionsDialog
        open={showExcelOptionsDialog}
        onClose={handleExcelDialogClose}
        title="יבוא צ’קליסט מאקסל"
        isDuplicateErr={isDuplicateErr}
        isLoading={isAdding}
        onOptionClick={handleOptionClick}
      />
    </>
  );
}

export default AddChecklists;
